/**
 * MarketingHistoryCard
 *
 * Who created, scheduled, sent, paused, resumed or edited a campaign or
 * automation, and when — newest first. Shared by the five marketing stats
 * pages; it fetches its own data, so a page only has to place it. Pages that
 * change state (pause, resume, resend) invalidate ['marketing-history'] so the
 * new row shows up without a reload.
 *
 * Automation edits are only recorded once an automation has left draft, and
 * nothing before this feature shipped was recorded at all — except a
 * campaign's creator, which campaigns have always kept.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useUserTimezone } from '@/hooks/useUserTimezone';
import { formatPacificDateTime } from '@/lib/facilityTimezone';
import * as marketingSvc from '@/services/marketing.service';
import type {
  MarketingHistoryChange,
  MarketingHistoryEvent,
  MarketingHistoryType,
} from '@/services/marketing.service';

const COLLAPSED_COUNT = 8;

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  description: 'Description',
  exitCriteria: 'Exit criteria',
  allowReentry: 'Allow re-entry',
  unsubscribeGroup: 'Unsubscribe group',
  categories: 'Categories',
  entryListId: 'Entry list',
  delay: 'Wait before sending',
  subject: 'Subject',
  preheader: 'Preheader',
  fromEmail: 'From email',
  fromName: 'From name',
  message: 'Message',
  fromNumber: 'From number',
};

/** Fields whose values history does not keep, only the fact they changed. */
const CONTENT_LABELS: Record<string, string> = {
  content: 'Email content changed',
  media: 'Images / media changed',
};

type Tone = 'go' | 'hold' | 'bad' | 'edit' | 'neutral';

const ACTION_TONE: Record<string, Tone> = {
  send: 'go', activate: 'go', resume: 'go', reactivate: 'go', resume_send: 'go', retry_failed: 'go', resend_failed: 'go',
  pause: 'hold', schedule: 'hold', reschedule: 'hold', unschedule: 'hold',
  stop: 'bad', delete: 'bad', send_failed: 'bad',
  update: 'edit', step_add: 'edit', step_update: 'edit', step_delete: 'edit', step_reorder: 'edit',
};

const TONE_DOT: Record<Tone, string> = {
  go: 'bg-emerald-500',
  hold: 'bg-amber-400',
  bad: 'bg-red-500',
  edit: 'bg-sky-500',
  neutral: 'bg-slate-300',
};

// ─── Formatting ──────────────────────────────────────────────────────────────
// History timestamps (and a schedule's send time) are Pacific wall-clock, like
// every legacy DATETIME; formatPacificDateTime shows them in the viewer's zone.

function formatDelay(minutesText: string | undefined): string {
  const minutes = Number(minutesText);
  if (!Number.isFinite(minutes) || minutes <= 0) return 'Immediately';
  const unit = minutes % 1440 === 0 ? ['day', 1440] : minutes % 60 === 0 ? ['hour', 60] : ['minute', 1];
  const n = minutes / (unit[1] as number);
  return `${n} ${unit[0]}${n === 1 ? '' : 's'}`;
}

function formatValue(change: MarketingHistoryChange, side: 'from' | 'to', isSms: boolean): string {
  const raw = change[side] ?? '';
  switch (change.field) {
    case 'exitCriteria':
      return raw === '1' ? 'Removed from the list' : isSms ? 'Received all messages' : 'Received all emails';
    case 'allowReentry':
      return raw === '1' ? 'Yes' : 'No';
    case 'delay':
      return formatDelay(raw);
    default:
      return raw === '' ? '(empty)' : `“${raw}”`;
  }
}

function plural(n: number, word: string): string {
  return `${n.toLocaleString()} ${word}${n === 1 ? '' : 's'}`;
}

function describe(e: MarketingHistoryEvent, tz: string): string {
  const d = e.details ?? {};
  const step = d.stepOrder ? `step ${d.stepOrder}` : 'a step';
  switch (e.action) {
    case 'create': return d.clonedFromName ? `Created as a copy of “${d.clonedFromName}”` : 'Created';
    case 'schedule': return `Scheduled to send ${formatPacificDateTime(d.scheduledAt, tz)}`;
    case 'reschedule': return `Rescheduled to send ${formatPacificDateTime(d.scheduledAt, tz)}`;
    case 'unschedule': return 'Unscheduled (back to draft)';
    case 'send': return d.via === 'schedule' ? 'Scheduled send started' : 'Sent';
    case 'send_failed': return d.via === 'schedule' ? 'Scheduled send failed' : 'Send failed';
    case 'resume_send': return 'Resumed a stalled send';
    case 'retry_failed': return `Retried ${plural(d.count ?? 0, 'failed contact')}`;
    case 'resend_failed': return 'Resent to failed recipients';
    case 'activate': return 'Activated';
    case 'resume': return 'Resumed';
    case 'reactivate': return 'Reactivated after being stopped';
    case 'pause': return 'Paused';
    case 'stop': return 'Stopped';
    case 'delete': return 'Deleted';
    case 'update': return 'Changed settings';
    case 'step_add': return `Added ${step}`;
    case 'step_update': return `Edited ${step}`;
    case 'step_delete': return `Removed ${step}`;
    case 'step_reorder': return 'Reordered steps';
    default: return e.action;
  }
}

/** The secondary lines under an event: counts, errors, which step. */
function notes(e: MarketingHistoryEvent): string[] {
  const d = e.details ?? {};
  const out: string[] = [];
  if (e.action === 'send' && d.recipients != null) {
    out.push(`To ${plural(d.recipients, 'recipient')}` + (d.noDevice ? ` · ${plural(d.noDevice, 'patient')} without a device` : ''));
  }
  if (e.action === 'send_failed' && d.error) out.push(d.error);
  if (e.action === 'resume_send' && d.queued != null) out.push(`${plural(d.queued, 'contact')} re-queued`);
  if (e.action === 'resend_failed') {
    out.push(`${(d.requeued ?? 0).toLocaleString()} re-queued` + (d.stillNoDevice ? ` · ${d.stillNoDevice.toLocaleString()} still without a device` : ''));
  }
  if (e.action.startsWith('step_') && (d.subject || d.message)) out.push(`“${d.subject || d.message}”`);
  if (e.action === 'step_reorder' && d.order) {
    const moves = d.order
      .map((oldNumber, i) => (oldNumber !== i + 1 ? `Step ${oldNumber} → position ${i + 1}` : null))
      .filter(Boolean) as string[];
    out.push(moves.join(' · '));
  }
  return out;
}

// ─── Component ───────────────────────────────────────────────────────────────

interface MarketingHistoryCardProps {
  type: MarketingHistoryType;
  id: number;
  className?: string;
}

export function MarketingHistoryCard({ type, id, className }: MarketingHistoryCardProps) {
  const tz = useUserTimezone();
  const [expanded, setExpanded] = useState(false);
  const isSms = type.startsWith('sms_');

  const { data: events = [], isLoading, isError } = useQuery({
    queryKey: ['marketing-history', type, id],
    queryFn: () => marketingSvc.getMarketingHistory(type, id),
    enabled: id > 0,
  });

  const visible = expanded ? events : events.slice(0, COLLAPSED_COUNT);

  return (
    <div className={cn('bg-white border border-[#e5e5e5] rounded-lg overflow-hidden', className)}>
      <div className="flex items-center gap-2 px-5 py-[15px] border-b border-[#e5e5e5] font-semibold text-[#333]">
        History
        <span
          className="w-4 h-4 rounded-full bg-[#e5e5e5] text-[#666] text-[11px] font-normal flex items-center justify-center cursor-help"
          title="Who created, scheduled, sent, paused, resumed or changed this, and when. Edits to an automation are tracked once it has been activated."
        >
          i
        </span>
        {events.length > 0 && <span className="ml-auto text-xs font-normal text-slate-500">{plural(events.length, 'event')}</span>}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
        </div>
      ) : isError ? (
        <p className="px-5 py-6 text-sm text-slate-500">Couldn't load the history.</p>
      ) : events.length === 0 ? (
        <p className="px-5 py-6 text-sm text-slate-500">
          No history recorded yet. Actions are recorded from now on; earlier ones weren't tracked.
        </p>
      ) : (
        <>
          <ul>
            {visible.map((e) => (
              <HistoryRow key={`${e.id}-${e.createdAt}-${e.action}`} event={e} tz={tz} isSms={isSms} />
            ))}
          </ul>
          {events.length > COLLAPSED_COUNT && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="w-full px-5 py-2.5 text-sm font-medium text-[#00aff0] bg-[#f8f9fa] border-t border-[#e5e5e5] hover:bg-slate-100"
            >
              {expanded ? 'Show less' : `Show all ${events.length}`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

function HistoryRow({ event, tz, isSms }: { event: MarketingHistoryEvent; tz: string; isSms: boolean }) {
  const changes = event.details?.changes ?? [];
  const scheduledSend = event.action.startsWith('send') && event.details?.via === 'schedule';
  const who = event.userName || (event.userId ? `User #${event.userId}` : 'System');

  return (
    <li className="flex gap-3 px-5 py-3 border-b border-[#f0f0f0] last:border-b-0">
      <span className={cn('mt-1.5 h-2 w-2 shrink-0 rounded-full', TONE_DOT[ACTION_TONE[event.action] ?? 'neutral'])} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-0.5">
          <span className="text-sm font-medium text-slate-800">{describe(event, tz)}</span>
          <span className="text-xs text-slate-500 whitespace-nowrap">{formatPacificDateTime(event.createdAt, tz)}</span>
        </div>
        <div className="text-xs text-slate-500 mt-0.5">
          {scheduledSend ? `Scheduled by ${who}` : `By ${who}`}
        </div>
        {notes(event).map((line, i) => (
          <div key={i} className="text-xs text-slate-500 mt-0.5 break-words">{line}</div>
        ))}
        {changes.length > 0 && (
          <ul className="mt-1.5 space-y-1">
            {changes.map((c, i) => (
              <ChangeLine key={i} change={c} isSms={isSms} />
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

function ChangeLine({ change, isSms }: { change: MarketingHistoryChange; isSms: boolean }) {
  if (CONTENT_LABELS[change.field]) {
    return <li className="text-xs text-slate-600">{CONTENT_LABELS[change.field]}</li>;
  }

  const label = FIELD_LABELS[change.field] ?? change.field;

  // A message body is too long to read inline, so before and after stack.
  if (change.field === 'message') {
    return (
      <li className="text-xs text-slate-600">
        <span className="font-medium">{label} changed</span>
        <div className="mt-1 grid gap-1 sm:grid-cols-2">
          <div className="rounded bg-slate-50 border border-slate-200 px-2 py-1.5 whitespace-pre-wrap break-words">
            <span className="block text-[10px] uppercase tracking-wide text-slate-400">Before</span>
            {change.from || '(empty)'}
          </div>
          <div className="rounded bg-slate-50 border border-slate-200 px-2 py-1.5 whitespace-pre-wrap break-words">
            <span className="block text-[10px] uppercase tracking-wide text-slate-400">After</span>
            {change.to || '(empty)'}
          </div>
        </div>
      </li>
    );
  }

  return (
    <li className="text-xs text-slate-600 break-words">
      <span className="font-medium">{label}:</span> {formatValue(change, 'from', isSms)} → {formatValue(change, 'to', isSms)}
    </li>
  );
}
